import { useEffect } from 'react';
import { paymentPageLanguage } from '@/lib/payment-page-language';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { XCircle } from 'lucide-react';
import { LanguageSelector } from '@/components/LanguageSelector';
import { Button } from '@/components/ui/button';

export default function PaymentFailed() {
  const [searchParams] = useSearchParams();
  const { t, i18n } = useTranslation('payments');

  // Payment result pages are customer-facing — respect lang param from edge function
  const langParam = paymentPageLanguage(searchParams.get('lang'));
  useEffect(() => {
    if (langParam && langParam !== i18n.language) {
      i18n.changeLanguage(langParam);
    }
  }, [langParam, i18n]);

  const errorCode = searchParams.get('code') || searchParams.get('error');

  // Forward-compatible: none of the three redirect sources (mercadopago-webhook,
  // mercadopago-create-payment, transbank-webhook-return) append the vendor's
  // WhatsApp number to this URL today (checked by reading all three), so this
  // never renders yet. The moment one of them adds ?phone=..., the customer gets
  // a real, working "back to chat" button instead of the close-tab fallback.
  const vendorPhone = searchParams.get('phone');
  const whatsappHref = vendorPhone
    ? `https://wa.me/${vendorPhone.replace(/[^0-9]/g, '')}?text=${encodeURIComponent(t('resultFailed.whatsappPrefill'))}`
    : null;

  // A code with no known human translation is not hidden -- it is named
  // honestly as "no more detail available", never invented (P-15).
  const primaryMessage = errorCode
    ? t('resultFailed.declinedNoDetail')
    : t('resultFailed.notCharged');

  return (
    <div className="min-h-screen bg-gradient-to-br from-red-50 to-red-100 dark:from-red-950 dark:to-gray-900 flex flex-col items-center justify-center p-4">
      <div className="absolute top-4 right-4">
        <LanguageSelector />
      </div>
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl p-8 max-w-md w-full text-center">
        <XCircle className="w-20 h-20 text-red-500 mx-auto mb-6" />
        <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100 mb-2">
          {t('resultFailed.title')}
        </h1>
        <p className="text-gray-600 dark:text-gray-400 mb-6">
          {primaryMessage}
        </p>

        {errorCode && (
          <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4 mb-6">
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-1">{t('resultFailed.errorCode')}</p>
            <p className="text-lg font-mono font-semibold text-gray-900 dark:text-gray-100">
              {errorCode}
            </p>
          </div>
        )}

        {/* The store's webhook does not leave the customer stranded: on a failed
            payment it mints a fresh payment link and sends it to their WhatsApp
            (transbank-webhook-return.ts sendWhatsAppFailure, up to 3 retries).
            This screen used to hide that, which is what made it read as a dead
            end. Saying it is the real exit — it needs no URL parameter and it
            is true for every customer who got here (P-17). */}
        <div className="bg-green-50 dark:bg-green-950/40 border border-green-200 dark:border-green-900 rounded-lg p-4 mb-6 text-left">
          <p className="text-sm text-green-900 dark:text-green-100 font-medium mb-1">
            {t('resultFailed.retrySentToWhatsapp')}
          </p>
          <p className="text-sm text-green-800 dark:text-green-200">
            {t('resultFailed.retrySentToWhatsappHint')}
          </p>
        </div>

        <p className="text-sm text-gray-600 dark:text-gray-400 mb-6">
          {t('resultFailed.contactSupport')}
        </p>

        <div className="flex flex-col gap-3">
          {whatsappHref && (
            <Button
              asChild
              className="w-full bg-green-600 hover:bg-green-700 dark:bg-green-700 dark:hover:bg-green-600"
            >
              <a href={whatsappHref} target="_blank" rel="noopener noreferrer">
                {t('resultFailed.backToChat')}
              </a>
            </Button>
          )}
          {/* window.close() only works for script-opened tabs; the WhatsApp
              guidance above is the real exit, this stays as a convenience. */}
          <Button variant="outline" onClick={() => window.close()}>
            {t('resultFailed.close')}
          </Button>
        </div>
      </div>

      <a
        href="https://okostec.ai"
        target="_blank"
        rel="noopener noreferrer"
        className="mt-6 flex items-center gap-2 text-gray-400 dark:text-gray-500 text-xs hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
      >
        <span>{t('poweredBy')}</span>
        <img
          src="https://storage.googleapis.com/gpt-engineer-file-uploads/YOgwp6cg3QOXTUHfe0Qjv3JHvT03/uploads/1757601334821-O.png"
          alt="Okostec"
          className="h-4 w-4"
        />
        <span className="font-medium text-gray-500 dark:text-gray-400">Okostec</span>
      </a>
    </div>
  );
}
